import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormGroup, MenuItem } from '@mui/material';

import {
  FIELD_NAMES,
  SupportedFormItems
} from '../common/fields';

import Select from '@/shared/molecules/Select';
import Input from '@/shared/molecules/Input';
import StoreSelector from '@/shared/organisms/StoreSelector';

import css from './ContactFormField.module.css';

interface ContactFormFieldProps {
  field: ContactFormFieldData;
  optional?: boolean;
  control?: UseFormReturn['control'];
  errors?: UseFormReturn['formState']['errors'];
  trigger?: UseFormReturn['trigger'];
  formState?: UseFormReturn['formState'];
  onSelectionChange?: (name: string, value: string) => void;
}

const ContactFormField: React.FC<ContactFormFieldProps> = (props: ContactFormFieldData) => {
  const {
    field, control, errors = [], trigger, onSelectionChange
  } = props;

  const {
    id = '',
    name = '',
    type: fieldType,
    label = '',
    required = false,
    options = [],
    optional = false,
    inputMode = 'text',
    extraInformation = '',
    maxLength = -1,
    showCharacterCount = false,
    minRows = 1,
    multiline = false,
    startAdornment = '',
    pattern = ''
  } = field || {};

  if (name === FIELD_NAMES.STORE_LOCATION) {
    return (
      <StoreSelector
        key={id}
        name={FIELD_NAMES.STORE_LOCATION}
        id={id}
        control={control}
        required={required}
      />
    );
  }

  switch (fieldType as ContactFormSupportedFormItems) {
    case SupportedFormItems.SELECT: {
      return (
        <FormGroup key={id}>
          <Select
            id={id}
            name={name}
            label={label}
            required={required}
            control={control}
            errors={errors}
            trigger={trigger}
            onSelectionChange={onSelectionChange}
            controlClass={css.select}
          >
            {options?.map((option: ContactFormFieldOption) => (
              <MenuItem key={option?.value} value={option?.value}>
                {option?.label}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>
      );
    }
    case SupportedFormItems.TEXTAREA:
    case SupportedFormItems.INPUT:
    case SupportedFormItems.DATE_PICKER: {
      return (
        <Input
          key={id}
          id={id}
          name={name}
          type={inputMode}
          ariaLabel={label}
          multiline={multiline}
          minRows={minRows}
          maxLength={maxLength}
          control={control}
          errors={errors}
          required={required}
          showCharacterCount={showCharacterCount}
          extraInformation={extraInformation}
          showOptionalLabel={optional}
          startAdornment={startAdornment}
          pattern={pattern}
        />
      );
    }

    default:
      return null;
  }
};

export default ContactFormField;
